import { CheckIcon } from "@storytell/bsky-icons";
import { PromptStaticRenderer } from "@storytell/rich-text";
import type { StreamingPrompt } from "@storytell/streaming";
import type { Asset, AssetId, Concept, ConceptId, Label, LabelId, View, ViewId } from "@storytell/types";
import { AnimateStaggeredChildren } from "@storytell/ui/animation";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbCopy } from "solid-icons/tb";
import { type Component, Show, createMemo, createSignal } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { Avatar } from "~/components/avatar/Avatar";
import { ScopedItemsList } from "~/components/prompt/internal/components/ScopedItemsList";
import { VariableV1StaticRenderer } from "~/components/prompt/internal/components/VariableV1Renderer/VariableV1Renderer";
import { useCopy } from "~/components/ui";
import { useWire } from "~/wire";
import { QuoteDisplay } from "../components/QuoteDisplay";
import { extractQuoteFromPrompt } from "../utils/extractQuote";

interface PromptAckRendererProps {
  prompt: StreamingPrompt;
  onClick?: () => void;
}

/**
 * Renders prompt acknowledgment blocks - displays the user's prompt with editing capabilities.
 * This is a special renderer that shows the user's avatar and prompt text in a distinct style.
 */
export const PromptAckRenderer: Component<PromptAckRendererProps> = (props) => {
  const wire = useWire();
  const [hovered, setHovered] = createSignal(false);
  const { copied, copy } = useCopy({ timeout: 1000 });

  const extractedData = createMemo(() => extractQuoteFromPrompt(props.prompt.richText));
  const quote = () => extractedData().quote;
  const contentWithoutQuote = () => extractedData().contentWithoutQuote;

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    await copy(props.prompt.text);
  };

  const messageAuthor = () => wire.stream.message.get(props.prompt.messageId)?.createdBy;
  const isCurrentUser = () => {
    const authorId = messageAuthor();
    const userId = wire.auth.userId();
    return authorId && userId && authorId === userId;
  };

  return (
    <>
      {/* Display quote outside and above the message panel */}
      <Show when={quote()}>
        <div class="mb-0">
          <QuoteDisplay quote={quote()!} />
        </div>
      </Show>

      {/* Message panel */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        data-renderer="prompt-ack-renderer"
        class={twMerge(
          "flex w-full items-start gap-4 py-4 px-6 mb-5 bg-panel hover:bg-panel-hover border border-primary-border relative group",
          quote() ? "rounded-b-2xl" : "rounded-2xl",
        )}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={props.onClick}
      >
        <div class="flex-1">
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between gap-2">
              <div class="grid grid-cols-[max-content_max-content_1fr] gap-2 items-center">
                <Show when={isCurrentUser()} fallback={<Avatar size="2xs" name="Someone else" />}>
                  <Avatar size="2xs" />
                </Show>

                <span class="text-sm text-secondary-text shrink-0">
                  <Show when={isCurrentUser()} fallback="Someone else">
                    {wire.auth.isGuest() ? "You" : wire.auth.user()?.displayName}
                  </Show>
                </span>

                <Show
                  when={
                    (props.prompt.scope?.assetIdList?.length ?? 0) +
                      (props.prompt.scope?.conceptIdList?.length ?? 0) +
                      (props.prompt.scope?.labelIdList?.length ?? 0) +
                      (props.prompt.scope?.viewIdList?.length ?? 0) >
                    0
                  }
                >
                  {(() => {
                    const scopeViews = () =>
                      (props.prompt.scope?.viewIdList ?? [])
                        .map((id) => wire.views.get(id as ViewId))
                        .filter((v) => v !== undefined) as View[];
                    const scopeLabels = () =>
                      (props.prompt.scope?.labelIdList ?? [])
                        .map((id) => wire.labels.get(id as LabelId))
                        .filter((l) => l !== undefined) as Label[];
                    const scopeConcepts = () =>
                      (props.prompt.scope?.conceptIdList ?? [])
                        .map((id) => wire.concepts.get(id as ConceptId))
                        .filter((c) => c !== undefined) as Concept[];
                    const scopeAssets = () =>
                      (props.prompt.scope?.assetIdList ?? [])
                        .map((id) => wire.assets.get(id as AssetId))
                        .filter((a) => a !== undefined) as Asset[];

                    return (
                      <div class="ml-2 text-xs max-w-full overflow-x-auto">
                        <ScopedItemsList
                          views={scopeViews}
                          labels={scopeLabels}
                          concepts={scopeConcepts}
                          assets={scopeAssets}
                          allowRemove={false}
                        />
                      </div>
                    );
                  })()}
                </Show>
              </div>

              <AnimateStaggeredChildren
                show={hovered()}
                enter={{
                  opacity: [0, 1],
                  y: [-5, 0],
                }}
                stagger={40}
                exit={{
                  opacity: [1, 0],
                  y: [0, -5],
                }}
              >
                {(animProps) => (
                  <div class="relative">
                    <div class="absolute -top-4 -right-2 flex items-center gap-2 text-xs">
                      <StTooltip
                        placement="top"
                        content={<div>{copied() ? "Copied!" : "Copy prompt to clipboard"}</div>}
                      >
                        <StTooltipTrigger
                          ref={animProps.ref}
                          type="button"
                          class="group/btn shadow-md cursor-pointer p-1 rounded-lg bg-tile hover:bg-tile-hover border border-muted-border"
                          onClick={handleCopy}
                        >
                          <Dynamic
                            component={copied() ? CheckIcon : TbCopy}
                            size={16}
                            class="group-hover/btn:scale-hover group-active/btn:scale-active transition-transform stroke-2"
                          />
                        </StTooltipTrigger>
                      </StTooltip>
                    </div>
                  </div>
                )}
              </AnimateStaggeredChildren>
            </div>

            {/* Display remaining content */}
            <Show
              when={contentWithoutQuote().content.length > 0}
              fallback={
                <Show when={!quote()}>
                  <div class="text-base font-medium text-primary-text">{props.prompt.text}</div>
                </Show>
              }
            >
              <PromptStaticRenderer doc={contentWithoutQuote()} variableV1Renderer={VariableV1StaticRenderer} />
            </Show>
          </div>
        </div>
      </div>
    </>
  );
};
